import { Badge } from '@/components/shadcn/badge'
import { SortingHeaderButton } from '@/components/ui/DataTable/SortingHeaderButton'
import { RestSchemas } from '@/contexts/QueryProvider'
import {
  coerceRequestStatus,
  coerceTicketType,
  getRequestStatusLabel,
  getTicketTypeLabel,
  REQUEST_STATUS_CLOSED,
} from '@/pages/Subscriptions/Requests/types'
import { ColumnDef } from '@tanstack/react-table'
import { PlatformTicketRequestRowActions } from './PlatformTicketRequestRowActions'
import { formatCreatedBy, getTicketSubject } from './requestDetailUtils'
import { formatMappedName } from './ticketLookups'
import { useTicketLookupMaps } from './TicketLookupsContext'

type ExistingRequest = RestSchemas['Messages.Requests.ExistingRequest'] & { subject?: string | null }

function SubjectCell({ request }: { request: ExistingRequest }) {
  const lookups = useTicketLookupMaps()
  return (
    <>
      {getTicketSubject(
        request.ticketType,
        request.ticket,
        request.subject,
        lookups.projectNames,
        lookups.applicationNames
      )}
    </>
  )
}

function OrganisationCell({ organisationId }: { organisationId?: number | null }) {
  const lookups = useTicketLookupMaps()
  if (organisationId == null) return <>—</>
  return <>{formatMappedName(organisationId, lookups.organisationNames, 'Organisation')}</>
}

function CreatedByCell({ userId }: { userId?: number | null }) {
  const lookups = useTicketLookupMaps()
  return <>{formatCreatedBy(userId, lookups)}</>
}

export function getPlatformTicketRequestColumns(
  onViewDetails: (request: ExistingRequest) => void
): ColumnDef<ExistingRequest>[] {
  return [
    {
      id: 'ticketType',
      accessorKey: 'ticketType',
      header: ({ column }) => <SortingHeaderButton column={column}>Type</SortingHeaderButton>,
      cell: ({ row }) => getTicketTypeLabel(row.original.ticketType),
      filterFn: (row, _columnId, filterValue: number[] | undefined) => {
        if (!filterValue?.length) return true
        const ticketType = coerceTicketType(row.original.ticketType)
        return ticketType != null && filterValue.includes(ticketType)
      },
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => <SortingHeaderButton column={column}>Status</SortingHeaderButton>,
      cell: ({ row }) => (
        <Badge variant={coerceRequestStatus(row.original.status) === REQUEST_STATUS_CLOSED ? 'secondary' : 'default'}>
          {getRequestStatusLabel(row.original.status)}
        </Badge>
      ),
      filterFn: (row, _columnId, filterValue: number[] | undefined) => {
        if (!filterValue?.length) return true
        const status = coerceRequestStatus(row.original.status)
        return status != null && filterValue.includes(status)
      },
    },
    {
      id: 'subject',
      accessorFn: (row) => row.subject ?? '',
      header: ({ column }) => <SortingHeaderButton column={column}>Name</SortingHeaderButton>,
      cell: ({ row }) => <SubjectCell request={row.original} />,
    },
    {
      id: 'organisationId',
      accessorKey: 'organisationId',
      header: ({ column }) => <SortingHeaderButton column={column}>Organisation</SortingHeaderButton>,
      cell: ({ row }) => <OrganisationCell organisationId={row.original.organisationId} />,
    },
    {
      id: 'createdAt',
      accessorKey: 'createdAt',
      header: ({ column }) => <SortingHeaderButton column={column}>Created</SortingHeaderButton>,
      cell: ({ row }) => (row.original.createdAt ? new Date(row.original.createdAt).toLocaleString() : ''),
    },
    {
      id: 'createdBy',
      accessorKey: 'createdBy',
      header: 'Created by',
      cell: ({ row }) => <CreatedByCell userId={row.original.createdBy} />,
    },
    {
      id: 'actions',
      meta: { disableCellTruncate: true },
      size: 72,
      cell: ({ row }) => (
        <div className="flex justify-end pr-5">
          <PlatformTicketRequestRowActions
            request={row.original}
            onViewDetails={onViewDetails}
          />
        </div>
      ),
    },
  ]
}
